<template>
  <div class="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-[#1C2128]">
    <table class="w-full table-fixed text-left text-[13px]">
      <thead>
        <tr class="border-b border-gray-100 dark:border-gray-700 text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
          <th v-for="column in COLUMNS" :key="column.key" scope="col" :aria-sort="ariaSort(column)" class="px-3 py-2.5 font-semibold whitespace-nowrap first:pl-4" :class="column.width">
            <button type="button" @click="sortBy(column.key)"
              class="inline-flex items-center gap-1 uppercase tracking-wider font-semibold hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none focus-visible:text-blue-600"
              :class="sort.key === column.key ? 'text-gray-700 dark:text-gray-200' : ''">
              {{ column.label }}
              <span class="w-2.5 text-[9px]" aria-hidden="true">{{ sort.key === column.key ? (sort.dir === 'asc' ? '▲' : '▼') : '' }}</span>
            </button>
          </th>
          <th scope="col" class="w-12 px-3 py-2.5"><span class="sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
        <tr v-for="row in rows" :key="row.resume.id" class="hover:bg-gray-50 dark:hover:bg-gray-800/50 align-top">
          <!-- The resume: name, its badges, the headline and when it was made. -->
          <td class="pl-4 pr-3 py-3 min-w-0">
            <RouterLink :to="`/cv_builder/${row.resume.id}`" data-turbo-prefetch="false" class="flex items-center gap-1.5 min-w-0 focus:outline-none focus-visible:underline">
              <span class="font-semibold text-gray-900 dark:text-white truncate">{{ row.resume.name }}</span>
              <span v-if="row.resume.is_default" class="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300">Default</span>
              <span v-else-if="row.resume.is_template" title="AI can write new resumes from this one" class="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300">Template</span>
              <span v-if="row.ai" class="flex-shrink-0 inline-flex items-center h-5 px-2 rounded-full text-[10px] font-semibold text-white bg-gradient-to-r from-blue-500 via-violet-500 to-fuchsia-500">AI generated</span>
            </RouterLink>
            <p class="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{{ row.headline || 'No headline yet' }}</p>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-0.5" :title="fullDate(row.resume.created_at)">{{ templateLabel(row.resume.template) }} · Created {{ shortDate(row.resume.created_at) }}</p>
          </td>

          <td class="px-3 py-3 min-w-0">
            <RouterLink v-if="row.resume.job" :to="`/jobs?job=${row.resume.job.id}`" data-turbo-prefetch="false" class="block truncate text-violet-700 dark:text-violet-300 hover:underline" :title="row.forJob">{{ row.forJob }}</RouterLink>
            <span v-else-if="row.forJob" class="block truncate text-gray-700 dark:text-gray-200" :title="row.forJob">{{ row.forJob }}</span>
            <span v-else class="text-gray-300 dark:text-gray-600">None</span>
          </td>

          <td class="px-3 py-3 whitespace-nowrap tabular-nums">
            <template v-if="row.newScore !== null">
              <span v-if="row.oldScore !== null" class="text-gray-400 dark:text-gray-500">{{ row.oldScore }}% → </span><span class="font-semibold" :class="scoreClass(row.newScore)">{{ row.newScore }}%</span>
            </template>
            <span v-else class="text-gray-300 dark:text-gray-600">None</span>
          </td>

          <!-- What the AI run used, in three short lines. -->
          <td class="px-3 py-3 min-w-0">
            <template v-if="row.run">
              <p class="text-gray-700 dark:text-gray-200 truncate">{{ row.run.model }}</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 tabular-nums truncate" :title="tokenTitle(row.run)">{{ number(row.run.total_tokens) }} tokens<template v-if="row.cost"> · <span title="An estimate from OpenAI's prices for this model.">{{ row.cost }}</span></template></p>
              <p v-if="row.run.duration_seconds != null" class="text-xs text-gray-400 dark:text-gray-500 tabular-nums">Took {{ row.run.duration_seconds }}s</p>
            </template>
            <span v-else class="text-gray-300 dark:text-gray-600">None</span>
          </td>

          <td class="px-3 py-3 text-right">
            <div class="relative inline-block" :ref="(el) => setMenuRoot(row.resume.id, el)">
              <button type="button" :aria-label="`More actions for ${row.resume.name}`" aria-haspopup="menu" :aria-expanded="openId === row.resume.id"
                @click="openId = openId === row.resume.id ? null : row.resume.id"
                class="w-7 h-7 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 6.75a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm0 6.75a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm0 6.75a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z"/></svg>
              </button>
              <ul v-if="openId === row.resume.id" role="menu" @keydown.esc="openId = null"
                class="absolute right-0 top-full mt-1 z-30 w-48 py-1 text-left rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-lg">
                <li v-for="action in actionsFor(row.resume)" :key="action.event" role="none">
                  <button type="button" role="menuitem" @click="choose(action.event, row.resume)"
                    class="w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-50 dark:hover:bg-gray-800"
                    :class="action.danger ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-200'">{{ action.label }}</button>
                </li>
              </ul>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { templateLabel } from './templates.js'
import { formatCost } from 'utils/cost.js'

const props = defineProps({
  resumes: { type: Array, required: true },
})
const emit = defineEmits(['edit-details', 'make-default', 'toggle-template', 'duplicate', 'delete'])

const COLUMNS = [
  { key: 'name', label: 'Resume', width: 'w-[38%]' },
  { key: 'forJob', label: 'Written for', width: 'w-[26%]' },
  { key: 'newScore', label: 'Match', width: 'w-24' },
  { key: 'costUsd', label: 'AI usage', width: 'w-[22%]' },
]

const actionsFor = (resume) => [
  resume.is_default ? null : { event: 'make-default', label: 'Make default' },
  resume.is_default ? null : { event: 'toggle-template', label: resume.is_template ? 'Remove from templates' : 'Use as a template' },
  { event: 'duplicate', label: 'Duplicate' },
  { event: 'edit-details', label: 'Rename or change template' },
  { event: 'delete', label: 'Delete', danger: true },
].filter(Boolean)

const score = (value) => (typeof value === 'number' ? value : null)

// One row per resume, with what the table sorts on worked out once.
const all = computed(() => props.resumes.map((resume) => {
  const run = resume.generation
  return {
    resume,
    run,
    ai: resume.ai_generated,
    headline: resume.headline,
    forJob: resume.generated_for || resume.job?.title || '',
    oldScore: score(run?.old_match_score),
    newScore: score(run?.new_match_score ?? resume.ai_match_score),
    cost: formatCost(run?.cost_usd),
  }
}))

// The open actions menu, and a click anywhere else closes it.
const openId = ref(null)
const menuRoots = {}
const setMenuRoot = (id, el) => { if (el) menuRoots[id] = el; else delete menuRoots[id] }
const onOutside = (event) => {
  if (openId.value && !menuRoots[openId.value]?.contains(event.target)) openId.value = null
}
onMounted(() => document.addEventListener('pointerdown', onOutside))
onBeforeUnmount(() => document.removeEventListener('pointerdown', onOutside))

function choose(event, resume) {
  openId.value = null
  emit(event, resume)
}

// Newest edit first until a header is clicked.
const sort = reactive({ key: 'updated', dir: 'desc' })

const VALUE = {
  name: (row) => row.resume.name.toLowerCase(),
  forJob: (row) => row.forJob.toLowerCase() || null,
  newScore: (row) => row.newScore,
  costUsd: (row) => row.run?.cost_usd ?? null,
  updated: (row) => new Date(row.resume.updated_at).getTime(),
}

// Rows with nothing in the sorted column go last, whichever way it is sorted.
const rows = computed(() => {
  const value = VALUE[sort.key]
  const factor = sort.dir === 'asc' ? 1 : -1
  return [...all.value].sort((a, b) => {
    const left = value(a)
    const right = value(b)
    if (left === null && right === null) return 0
    if (left === null) return 1
    if (right === null) return -1
    return left < right ? -factor : left > right ? factor : 0
  })
})

function sortBy(key) {
  if (sort.key === key) sort.dir = sort.dir === 'asc' ? 'desc' : 'asc'
  else Object.assign(sort, { key, dir: 'asc' })
}

const ariaSort = (column) => (sort.key === column.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none')

const scoreClass = (value) => (value >= 75 ? 'text-green-600 dark:text-green-400' : value >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400')
const number = (value) => (value ?? 0).toLocaleString('en-US')
const tokenTitle = (run) => (run ? `${number(run.input_tokens)} in (${number(run.cached_input_tokens)} cached), ${number(run.output_tokens)} out` : undefined)
const shortDate = (value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const fullDate = (value) => new Date(value).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
</script>
